/**
 * RTK Query in-flight tracker feeding TTFD (AXI-3875).
 *
 * Every RTK Query slice dispatches lifecycle actions typed
 * `${reducerPath}/executeQuery/{pending,fulfilled,rejected}`. We watch those
 * suffixes across ALL slices from a single store-level middleware — no
 * per-endpoint `onQueryStarted` wiring — so the navigation tracker learns when
 * a screen's reads start and settle. Mutations (`executeMutation/*`) are
 * intentionally ignored: TTFD is about the data a screen needs to display, not
 * user-initiated writes.
 */
import type { Middleware } from '@reduxjs/toolkit'
import { onQueryStart, onQuerySettle } from './navigation-timing'

const QUERY_PENDING = '/executeQuery/pending'
const QUERY_FULFILLED = '/executeQuery/fulfilled'
const QUERY_REJECTED = '/executeQuery/rejected'

// Canonical redux `store => next => action` arity. The store is unused (this
// middleware only inspects action types), but declaring the param keeps the
// runtime signature in step with the `Middleware` type — and lets call sites
// pass the store without it being flagged as a superfluous argument.
export const queryInflightMiddleware: Middleware = (_store) => (next) => (action) => {
  const type = (action as { type?: unknown })?.type
  if (typeof type === 'string') {
    if (type.endsWith(QUERY_PENDING)) {
      onQueryStart()
    } else if (type.endsWith(QUERY_FULFILLED) || type.endsWith(QUERY_REJECTED)) {
      onQuerySettle()
    }
  }
  return next(action)
}
